import React from 'react';
import { BsCheck } from 'react-icons/bs';
import { FiEdit } from 'react-icons/fi';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useRouteMatch } from 'react-router-dom';
import { Flex, Icon, IconButton, Text, Tooltip } from '@chakra-ui/react';
import PropTypes from 'prop-types';

import ConfirmActionWarningModal from '../../../../../../components/ConfirmActionWarningModal';
import DropdownMenu from '../../../../../../components/DropdownMenu';
import { findSelected } from '../../../../../../components/DropdownMenu/util';
import StackedButtonGroup from '../../../../../../components/Modal/ModalButtonGroups/StackedButtonGroup';
import { analyticsIds } from '../../../../../../constants/analyticsIds';
import { CypressIds } from '../../../../../../constants/cypressIds';
import { Statuses } from '../../../../../../constants/statuses';
import { archiveJob, deleteJob, reactivateJob, selectCurrentlyUpdatingJob, selectJobProfile } from '../../../../../../redux/modules/jobs';
import { jobSearch } from '../../../../../../redux/modules/jobSearch';
import { hideModal, showModal, updateModalOptions } from '../../../../../../redux/modules/modal';
import { confirmArchiveWithProcoreWarning, loadProcoreDeleteWarning } from '../../../../../../utils/procoreWarningModalUtil';
import { getStatusDisplayName } from '../../../../../../utils/statusUtil';
import { createToast } from '../../../../../../utils/toastUtil';
import TransferArchivedJobModal from '../TransferArchivedJobModal';

const JobInfoHeader = ({ isEditing }) => {
  const dispatch = useDispatch();
  const jobProfile = useSelector(selectJobProfile);
  const currentlyUpdatingJob = useSelector(selectCurrentlyUpdatingJob);
  const history = useHistory();
  const { url } = useRouteMatch();

  const handleStatusUpdate = async selected => {
    const selectedStatus = findSelected(jobProfile.jobStatusOptions, selected.value);

    if (selectedStatus.id === Statuses.Deleted) {
      const { procoreWarning } = await loadProcoreDeleteWarning(dispatch, 'job');

      dispatch(showModal(
        () => (
          <ConfirmActionWarningModal
            description="This project will be permanently deleted along with all records and attachments."
            procoreWarning={procoreWarning}
          />
        ),
        {
          title: 'Delete Project?',
          size: 'md',
          buttons: {
            component: StackedButtonGroup,
            primaryButtonOptions: {
              label: 'Delete',
              isDisabled: true,
              onClick: async () => {
                dispatch(updateModalOptions({
                  actionRequired: true,
                  buttons: { primaryButtonOptions: { isLoading: true } }
                }));

                createToast({
                  title: 'Deleting Project...',
                  duration: 10000,
                  status: 'info'
                });

                await dispatch(deleteJob(jobProfile.jobId));
                await dispatch(jobSearch.getPage());
                dispatch(hideModal());
                history.push(url.replace(/projects.*/, 'projects'));
              }
            }
          }
        }
      ));
    } else if (jobProfile.department.archivedAt && selectedStatus.id === Statuses.Active) {
      dispatch(showModal(TransferArchivedJobModal, {
        title: 'Transfer Project to Active Department',
        size: 'lg'
      }));
    } else if (selectedStatus.id === Statuses.Archived) {
      const archive = () => {
        dispatch(hideModal());
        dispatch(archiveJob(jobProfile.jobId));
      };

      // Archiving a project had no confirmation. It gains one only where it
      // now has a consequence outside PINS; otherwise it archives at once, as
      // before.
      confirmArchiveWithProcoreWarning({
        dispatch,
        scope: 'job',
        description: 'Archiving this project archives its records.',
        onClick: archive,
        title: 'Archive Project?',
        label: 'Archive',
        fallback: () => dispatch(archiveJob(jobProfile.jobId))
      });
    } else {
      dispatch(reactivateJob(jobProfile.jobId));
    }
  };

  const isArchived = jobProfile.status === Statuses.Archived;

  return (
    <Flex
      alignItems="center"
      flexFlow="row nowrap"
      justifyContent="space-between"
      pb={6}
      textStyle="bodyMd600"
      width="100%"
    >
      <Text textAlign="left">Project Information</Text>
      <Flex alignItems="center" flexFlow="row nowrap">
        <Tooltip label={isEditing ? 'Save project' : 'Edit project'}>
          <IconButton
            aria-label={isEditing ? 'Save project' : 'Edit project'}
            data-analytics-id={analyticsIds.projectDetailsPage[isEditing ? 'saveButton' : 'editButton']}
            data-cy={isEditing ? CypressIds.saveProjectDetailsButton : CypressIds.editProjectDetailsButton}
            display={isArchived ? 'none' : ''}
            icon={<Icon as={isEditing ? BsCheck : FiEdit} boxSize="24px" />}
            isDisabled={currentlyUpdatingJob}
            mx={2}
            type="submit"
            variant={isEditing ? 'accentSuccess' : 'textPrimary'}
          />
        </Tooltip>
        <DropdownMenu
          dataAnalyticsKey={analyticsIds.projectDetailsPage.updateStatusDropdown}
          isDisabled={currentlyUpdatingJob}
          options={jobProfile.jobStatusOptions.filter(status => status.id !== jobProfile.status)}
          selected={getStatusDisplayName(jobProfile.status)}
          variant={isArchived ? 'accent' : 'secondary'}
          width="120px"
          onSelect={handleStatusUpdate}
        />
      </Flex>
    </Flex>
  );
};

JobInfoHeader.propTypes = { isEditing: PropTypes.bool };

export default JobInfoHeader;
